import { useEffect, useState } from 'react';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  TextField,
  toast,
} from '@eon/ui';
import { hooks } from '@eon/api-client';
import type { Device } from '@eon/types';

interface EditOmDeviceDialogProps {
  device: Device | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditOmDeviceDialog({ device, open, onOpenChange }: EditOmDeviceDialogProps) {
  const { t } = useTranslation();
  const update = hooks.useOmDeviceUpdate();
  const del = hooks.useOmDeviceDelete();
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (device && open) {
      setDisplayName(device.displayName?.trim() ?? '');
      setDescription(device.description?.trim() ?? '');
    }
  }, [device, open]);

  const onSave = async () => {
    if (!device) return;
    try {
      await update.mutateAsync({
        deviceId: device.id,
        displayName: displayName.trim() || undefined,
        description: description.trim() || undefined,
      });
      toast.success(t('portal.devices.updated'));
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  };

  const onDelete = () => {
    if (!device || !window.confirm(t('portal.devices.deleteConfirm'))) return;
    del.mutate(device.id, {
      onSuccess: () => {
        toast.success(t('portal.devices.deleted'));
        onOpenChange(false);
      },
      onError: (e) => toast.error(resolveApiError(e, t)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('portal.devices.editTitle')}</DialogTitle>
        </DialogHeader>
        {device ? (
          <div className="space-y-4">
            <p className="text-muted-foreground font-mono text-xs">{device.serialNumber}</p>
            <div className="space-y-1.5">
              <Label htmlFor="edit-device-name">{t('portal.devices.displayName')}</Label>
              <Input id="edit-device-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-device-desc">{t('portal.devices.description')}</Label>
              <TextField id="edit-device-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <DialogFooter className="sm:justify-between">
              <Button type="button" variant="destructive" onClick={onDelete} disabled={del.isPending}>
                {t('actions.delete')}
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  {t('actions.cancel')}
                </Button>
                <Button type="button" onClick={onSave} disabled={update.isPending}>
                  {t('actions.save')}
                </Button>
              </div>
            </DialogFooter>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
